import React, { useState } from 'react';
import {
  Typography,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Paper,
  Stack
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

// Import des composants séparés
import SubscriptionSection from './settings/SubscriptionSection';
import VisibilitySection from './settings/VisibilitySection';
import PaymentSection from './settings/PaymentSection';
import BookingSettingsSection from './settings/BookingSettingsSection';
import EmailSettingsSection from './settings/EmailSettingsSection';
import SecuritySection from './settings/SecuritySection';

const SettingsTab = ({ userProfile, setSuccess, setError }) => {
  const [expandedSection, setExpandedSection] = useState('subscription');
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  // Modern color palette
  const colors = {
    primary: '#000000',
    secondary: '#6B7280',
    accent: '#F5F5DC',
    background: '#F8F9FA',
    surface: '#FFFFFF',
    border: '#E5E7EB'
  };

  const settingSections = [
    {
      component: SubscriptionSection,
      props: { userProfile }
    },
    {
      component: VisibilitySection,
      props: { userProfile, setSuccess, setError, currentUser }
    },
    {
      component: PaymentSection,
      props: { userProfile, setSuccess, setError, currentUser }
    },
    {
      component: BookingSettingsSection,
      props: { userProfile, setSuccess, setError, currentUser }
    },
    {
      component: EmailSettingsSection,
      props: { userProfile, setSuccess, setError, currentUser }
    },
    {
      component: SecuritySection,
      props: { setSuccess, setError, currentUser, userProfile }
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      background: colors.background,
      py: { xs: 2, sm: 3, md: 4 }
    }}>
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 3, sm: 4, md: 6 }
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              color: colors.primary,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              mb: 2,
              letterSpacing: '-0.02em'
            }}
          >
            {t('dashboard.settings')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: colors.secondary,
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              maxWidth: '500px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Configurez votre salon selon vos besoins
          </Typography>
        </Box>

        {/* Settings Stack - Mobile First Approach */}
        {isMobile ? (
          // Mobile: Single column stack
          <Stack spacing={2}>
            {settingSections.map((section, index) => {
              const Component = section.component;
              return (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    background: colors.surface,
                    borderRadius: 3,
                    border: `1px solid ${colors.border}`,
                    overflow: 'hidden'
                  }}
                >
                  <Component
                    {...section.props}
                    expandedSection={expandedSection}
                    onAccordionChange={handleAccordionChange}
                    isMobile={isMobile}
                  />
                </Paper>
              );
            })}
          </Stack>
        ) : (
          // Desktop/Tablet: CSS Grid Layout
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                sm: 'repeat(auto-fit, minmax(350px, 1fr))',
                md: 'repeat(auto-fit, minmax(400px, 1fr))',
                lg: 'repeat(3, 1fr)'
              },
              gap: { xs: 2, sm: 3, md: 4 },
              alignItems: 'start'
            }}
          >
            {settingSections.map((section, index) => {
              const Component = section.component;
              return (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    background: colors.surface,
                    borderRadius: 3,
                    border: `1px solid ${colors.border}`,
                    overflow: 'hidden',
                    height: 'fit-content',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                      borderColor: colors.primary
                    }
                  }}
                >
                  <Component
                    {...section.props}
                    expandedSection={null}
                    onAccordionChange={() => {}}
                    isMobile={false}
                  />
                </Paper>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SettingsTab;